import { useMemo } from 'react';

import { ElevatedCard } from '@myphx-engineering/phx-design-system';

import { MedicationOrders } from '../../../components/cabinet/orders-card/MedicationOrders';
import { CabinetUpToDateCard } from '../../../components/cabinet/orders/CabinetUpToDateCard';
import { getFragment } from '../../../graphql';
import {
    type GetBasicPrescriberOrdersByPatientQuery,
    type GetBasicPrescriberOrdersQuery,
    type GetPatientPriorAuthorizationsQuery,
    type PriorAuthorizationInfoFragment,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePatientContext } from '../../../hooks/use-patient-context';
import {
    type PrescriberOrdersFilter,
    usePrescriberOrders,
} from '../../../hooks/use-prescriber-orders';

import { MedCabDetailsHeader } from './MedCabDetailsHeader';

export type MedCabCardProps = {
    patient:
        | NonNullable<GetBasicPrescriberOrdersQuery['patient']>
        | NonNullable<GetBasicPrescriberOrdersByPatientQuery['patient']>;
    patientId: string;
    onlyShowUnclaimedOrders: boolean;
    nonDrxPatientPriorAuthorizations: NonNullable<
        GetPatientPriorAuthorizationsQuery['patient']
    >;
};

const getOrderFilter = (
    includeDependents: boolean,
    onlyShowUnclaimedOrders: boolean
): PrescriberOrdersFilter => {
    if (onlyShowUnclaimedOrders) return 'unclaimed';
    if (includeDependents) return 'new-all';
    return 'new-by-patient';
};

export const MedCabDetailCard = ({
    patient,
    patientId,
    onlyShowUnclaimedOrders,
    nonDrxPatientPriorAuthorizations,
}: MedCabCardProps) => {
    const { nonDrxPriorAuthorizationEnabled } = useFlags();
    const { primaryPatientId, includeDependents } = usePatientContext();

    const nonDrxPriorAuths = getFragment(
        nonDrxPatientPriorAuthorizations.priorAuthorizationsConnection
    ).edges.map((edge) => edge.node as PriorAuthorizationInfoFragment);

    const orders = usePrescriberOrders({
        patient,
        patientId,
        primaryPatientId,
        nonDrxPriorAuthorizations: nonDrxPriorAuths,
        filter: getOrderFilter(includeDependents, onlyShowUnclaimedOrders),
    });

    const filteredOrders = useMemo(() => {
        if (!nonDrxPriorAuthorizationEnabled) {
            return orders.filter(
                (order) =>
                    !(
                        'applicablePriorAuthorization' in order &&
                        getFragment(order.applicablePriorAuthorization)
                            ?.isSpecialty === false
                    )
            );
        }
        return orders;
    }, [orders, nonDrxPriorAuthorizationEnabled]);
    if (filteredOrders.length === 0) {
        return <CabinetUpToDateCard />;
    }

    return (
        <ElevatedCard
            data-testid="medicine-cabinet-detail-card"
            header={<MedCabDetailsHeader />}
        >
            <MedicationOrders orders={filteredOrders} showAll />
        </ElevatedCard>
    );
};
